/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function EmbeddingVideos() {
  return (
    <>
      <Meta
        title="Working with oEmbed: Embedding Videos"
        description="Learn how to embed Vimeo videos on any site that supports the oEmbed standard."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Link href={'/api/oembed'}>Working with oEmbed</Path.Link>
            <Path.Current>Embedding Videos</Path.Current>
          </Path>
        }
        nav={{ page: 'oembed', subpage: 'videos' }}
      >
        <Header>Working with oEmbed: Embedding Videos</Header>

        <Paragraph size="2">
          If you have the URL to a Vimeo video, you can use oEmbed to generate an embeddable{' '}
          <code>{`<iframe>`}</code> element for the video in question. This tutorial shows you how.
        </Paragraph>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>The URL of a video on Vimeo.</strong> See below for more details.
          </li>
        </ul>

        <Header size="2" id="embedding-a-video-with-oembed">
          Embedding a video with oEmbed
        </Header>
        <Paragraph size="2">To embed a video with oEmbed, follow this general workflow:</Paragraph>

        <FancyListTableOfContents
          anchorPrefix="embedding-a-video-with-oembed"
          items={['Get an oEmbed response for a video.', 'Embed the video.']}
        />

        <Paragraph size="2">
          The corresponding sections break each step down with exactly the sort of exhaustive detail
          that you're looking for in an online tutorial.
        </Paragraph>

        <FancyList anchorPrefix="embedding-a-video-with-oembed">
          <FancyListItem>
            <Header size="3">Get an oEmbed response for a video</Header>
            <Paragraph size="2">
              To get an oEmbed response for a video on Vimeo, send a GET request to the following
              URL:
            </Paragraph>

            <CodeBlock
              endpoint={{
                httpPrefix: false,
                method: 'get',
                resource: 'https://vimeo.com/api/oembed.json?url={video_url}',
              }}
            />

            <StyledNotice>
              <strong>NOTE:</strong> Be sure to URL-encode the{' '}
              <code>
                <b>url</b>
              </code>{' '}
              value.
            </StyledNotice>

            <Paragraph size="2">
              The value of the{' '}
              <code>
                <b>url</b>
              </code>{' '}
              parameter can follow any of five URL schemes, depending on the type of video (see{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>
              ).
            </Paragraph>

            <GuideTable
              tableNumber={1}
              description="URL schemas"
              headers={[
                <span>
                  The video is<span>&hellip;</span>
                </span>,
                'URL scheme',
              ]}
              rows={[
                ['A regular Vimeo video', <code>{`https://vimeo.com/{video_id}`}</code>],
                [
                  'In a showcase',
                  <code>{`https://vimeo.com/album/{album_id}/video/{video_id}`}</code>,
                ],
                [
                  'On a channel',
                  <code>{`https://vimeo.com/channels/{channel_id}/{video_id}`}</code>,
                ],
                [
                  'In a group',
                  <code>{`https://vimeo.com/groups/{group_id}/videos/{video_id}`}</code>,
                ],
                [
                  'An On Demand video',
                  <code>{`https://vimeo.com/ondemand/{ondemand_id}/{video_id}`}</code>,
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> When you want to embed a video with the <code>unlisted</code>{' '}
              privacy setting, be sure to send the entire URL, not just the portion with the video
              ID. The URL of an unlisted video contains an additional string of characters that you
              must include with your GET request to receive embed data of any kind.
            </StyledNotice>

            <Paragraph size="2">
              You can use any of the arguments in{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>{' '}
              below as additional query parameters. Just be sure to URL-encode their values. While
              you're at it, we recommend that you specify the{' '}
              <code>
                <b>width</b>
              </code>{' '}
              and{' '}
              <code>
                <b>height</b>
              </code>{' '}
              parameters to create a consistent default size for your videos, like this:
            </Paragraph>

            <CodeBlock
              endpoint={{
                httpPrefix: false,
                method: 'get',
                resource:
                  'https://vimeo.com/api/oembed.json?url=https%3A//vimeo.com/286898202&width=480&height=360',
              }}
            />

            <Paragraph size="2">
              You can't override the video's default settings when the video owner's membership
              level is anything above Vimeo Free or Vimeo Basic legacy. (Refer to the{' '}
              <code>
                <b>account_type</b>
              </code>{' '}
              field in the oEmbed response to determine this information.) If you yourself are a
              video owner with a Vimeo Free or Vimeo Basic legacy membership, and you'd like to
              preserve your preferences across embeds, it might be worth your while to{' '}
              <Link variant="minimal" href="https://vimeo.com/upgrade">
                upgrade
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={2}
              description="oEmbed arguments"
              headers={['Argument', 'Default Value', 'Description']}
              rows={[
                [
                  <span>
                    <code>
                      <b>id</b>
                    </code>{' '}
                    <em>or</em>{' '}
                    <code>
                      <b>url</b>
                    </code>
                  </span>,
                  'None',
                  <span>
                    Either the ID or the URL of the video on Vimeo. You must supply one of these
                    values to identify the video. When the video's{' '}
                    <Link
                      variant="minimal"
                      href="https://vimeo.zendesk.com/hc/en-us/articles/224817847-Privacy-settings-overview"
                    >
                      privacy setting
                    </Link>{' '}
                    is <code>Unlisted</code>, you must use the URL, and the URL must include the{' '}
                    <code>
                      <b>h</b>
                    </code>{' '}
                    parameter. For more information, see our{' '}
                    <Link
                      variant="minimal"
                      href="https://developer.vimeo.com/player/sdk/basics#connecting-to-a-vimeo-player"
                    >
                      introductory guide
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>airplay</b>
                  </code>,
                  <code>true</code>,
                  'Whether AirPlay is enabled in the embeddable player.',
                ],
                [
                  <code>
                    <b>api</b>
                  </code>,
                  <code>true</code>,
                  <span>
                    Whether to enable the{' '}
                    <Link variant="minimal" href="/player/sdk">
                      Vimeo player SDK
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>audio_tracks</b>
                  </code>,
                  <code>true</code>,
                  "Whether multiple audio tracks can appear in the embeddable player. This option has no effect if your video doesn't have additional audio tracks uploaded to it.",
                ],
                [
                  <span>
                    <code>
                      <b>audiotrack</b>
                    </code>
                  </span>,
                  <span>None</span>,
                  <span>
                    The initial audio track to play with the video. The value of this option can be
                    the language code alone (<code>fr</code>), the language code and region (
                    <code>fr-ca</code>), or the language code and kind (<code>fr,descriptions</code>
                    ) of the desired audio. Set this value to <code>main</code> to select the
                    original audio for the video. If there is no matching track, the player ignores
                    this value.
                  </span>,
                ],
                [
                  <code>
                    <b>autopause</b>
                  </code>,
                  <code>true</code>,
                  <span>
                    Whether to pause the current video when another Vimeo video on the same page
                    starts to play. Set this value to <code>false</code> to permit simultaneous
                    playback of all the videos on the page. This option has no effect if you've
                    disabled cookies in your browser, either through browser settings or with an
                    extension or plugin.
                  </span>,
                ],
                [
                  <code>
                    <b>autoplay</b>
                  </code>,
                  <code>false</code>,
                  'Whether to start playback of the video automatically. This feature might not work on all devices.',
                ],
                [
                  <code>
                    <b>background</b>
                  </code>,
                  <code>false</code>,
                  'Whether the player is in background mode, which hides the playback controls, enables autoplay, and loops the video.',
                ],
                [
                  <code>
                    <b>byline</b>
                  </code>,
                  <code>true</code>,
                  "Whether to display the video owner's name.",
                ],
                [
                  <code>
                    <b>callback</b>
                  </code>,
                  'None',
                  'The name of the JavaScript function to use as the callback parameter of a JSONP call. The indicated function wraps the JSON response.',
                ],
                [
                  <code>
                    <b>cc</b>
                  </code>,
                  <code>true</code>,
                  "Whether closed captions are enabled in the embeddable player. This option has no effect if your video doesn't have captions or subtitles.",
                ],
                [
                  <code>
                    <b>chapter_id</b>
                  </code>,
                  'None',
                  'The chapter by ID to start playback at.',
                ],
                [
                  <code>
                    <b>chapters</b>
                  </code>,
                  <code>true</code>,
                  "Whether chapters are enabled in the embeddable player. This option has no effect if your video doesn't have chapters set.",
                ],
                [
                  <code>
                    <b>chromecast</b>
                  </code>,
                  <code>true</code>,
                  'Whether the Chromecast button appears in the embeddable player.',
                ],
                [
                  <code>
                    <b>color</b>
                  </code>,
                  'None',
                  <span>
                    The hexadecimal accent color value of the playback controls, which is normally{' '}
                    <code>00ADEF</code>. The embed settings of the video might override this value.
                    For more information about player colors, refer to the <code>colors</code>{' '}
                    option.
                  </span>,
                ],
                [
                  <code>
                    <b>colors</b>
                  </code>,
                  'None',
                  <span>
                    The hexadecimal color values of the player. The embed settings of the video
                    might override these values. The order of the player colors is{' '}
                    <code>[Primary, Accent, Text/Icon, Background]</code>, with corresponding
                    default values of <code>["000000", "00ADEF", "FFFFFF", "000000"]</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>controls</b>
                  </code>,
                  <code>true</code>,
                  <span>
                    Whether to display the player's interactive elements, including the play bar and
                    sharing buttons. Set this option to <code>false</code> for a chromeless
                    experience. To control playback when the play/pause button is hidden, set{' '}
                    <code>
                      <b>autoplay</b>
                    </code>{' '}
                    to <code>true</code>, use keyboard controls (which remain active), or implement
                    our player SDK.
                  </span>,
                ],
                [
                  <code>
                    <b>dnt</b>
                  </code>,
                  <code>false</code>,
                  <span>
                    Whether to prevent the player from tracking session data, including cookies.
                    Keep in mind that setting this argument to <code>true</code> also blocks{' '}
                    <Link variant="minimal" href="https://vimeo.com/stats">
                      video stats
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>end_time</b>
                  </code>,
                  'None',
                  <span>
                    In Segmented Playback mode, the time in seconds where playback ends for the
                    video. Setting this field enables Segmented Playback mode, where the video is
                    presented as a segment of the original as defined by this field and the{' '}
                    <code>
                      <b>start_time</b>
                    </code>{' '}
                    field, with a duration in seconds equal to{' '}
                    <code>
                      <b>end_time</b>
                    </code>{' '}
                    minus{' '}
                    <code>
                      <b>start_time</b>
                    </code>
                    . If{' '}
                    <code>
                      <b>start_time</b>
                    </code>{' '}
                    is omitted, the start time defaults to <code>0</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>fullscreen</b>
                  </code>,
                  <code>true</code>,
                  <span>Whether to show the fullscreen button in the embeddable player.</span>,
                ],
                [
                  <code>
                    <b>height</b>
                  </code>,
                  'None',
                  'The height of the video in pixels.',
                ],
                [
                  <code>
                    <b>initial_quality</b>
                  </code>,
                  'None',
                  <span>
                    The playback quality of the first seconds of video loaded. After that, normal
                    adaptive bitrate logic applies. Setting a lower quality can help to make
                    playback start more quickly, while setting a higher quality helps to ensure that
                    viewers receive high-resolution video as soon as the video begins playing. This
                    field can take any value from the quality menu in the player for the video.
                    Common values are <code>360p</code>, <code>720p</code>, <code>1080p</code>,{' '}
                    <code>2k</code>, and <code>4k</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>interactive_markers</b>
                  </code>,
                  <code>true</code>,
                  'Whether to display markers representing the timestamp where hotspots appear on an interactive video.',
                ],
                [
                  <code>
                    <b>interactive_params</b>
                  </code>,
                  'None',
                  <span>
                    Key-value pairs representing dynamic parameters that are utilized on interactive
                    videos with dynamic elements, such as{' '}
                    <code>title=my-video,subtitle=interactive</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>keyboard</b>
                  </code>,
                  <code>true</code>,
                  "Whether to enable keyboard input to trigger player events. This setting doesn't affect tab control.",
                ],
                [
                  <code>
                    <b>loop</b>
                  </code>,
                  <code>false</code>,
                  'Whether to restart the video automatically after reaching the end.',
                ],
                [
                  <code>
                    <b>maxheight</b>
                  </code>,
                  'None',
                  "The height of the video in pixels, where the video won't exceed its native height, no matter the value of this field.",
                ],
                [
                  <code>
                    <b>max_quality</b>
                  </code>,
                  'None',
                  <span>
                    The highest possible quality that the player automatically switches to during
                    video playback. The player determines the best quality to play at without
                    exceeding this maximum value. Setting this parameter can help to reduce
                    bandwidth usage. This field can take any value from the quality menu in the
                    player for the video. Common values are <code>360p</code>, <code>720p</code>,{' '}
                    <code>1080p</code>, <code>2k</code>, or <code>4k</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>maxwidth</b>
                  </code>,
                  'None',
                  "The width of the video in pixels, where the video won't exceed its native width, no matter the value of this field.",
                ],
                [
                  <code>
                    <b>min_quality</b>
                  </code>,
                  'None',
                  <span>
                    The lowest possible quality that the player automatically switches to during
                    video playback. The player determines the best quality to play at without
                    dropping below this minimum value. Setting this parameter can help to ensure
                    that the viewer receives high-resolution video while still accounting for
                    bandwidth. This field can take any value from the quality menu in the player for
                    the video. Common values are <code>360p</code>, <code>720p</code>,{' '}
                    <code>1080p</code>, <code>2k</code>, or <code>4k</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>muted</b>
                  </code>,
                  <code>false</code>,
                  <span>
                    Whether the video is muted upon loading. The <code>true</code> value is required
                    for the autoplay behavior in some browsers.
                  </span>,
                ],
                [
                  <code>
                    <b>pip</b>
                  </code>,
                  <code>true</code>,
                  'Whether to include the picture-in-picture button among the player controls and enable the Picture-in-Picture API, where supported.',
                ],
                [
                  <code>
                    <b>play_button_position</b>
                  </code>,
                  <code>auto</code>,
                  <span>
                    The position of the play button within the embeddable player. Other possible
                    values include <code>bottom</code> and <code>center</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>player_id</b>
                  </code>,
                  'None',
                  'The unique ID for the player, which comes back with all JavaScript API responses.',
                ],
                [
                  <code>
                    <b>playsinline</b>
                  </code>,
                  <code>true</code>,
                  'Whether the video plays inline on supported mobile devices.',
                ],
                [
                  <code>
                    <b>portrait</b>
                  </code>,
                  <code>true</code>,
                  "Whether to display the video owner's portrait.",
                ],
                [
                  <code>
                    <b>preload</b>
                  </code>,
                  <code>metadata_on_hover</code>,
                  <span>
                    The loading behavior for the player before playback is initiated. This follows
                    the basic principles of the native{' '}
                    <Link
                      variant="minimal"
                      href="https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/preload"
                    >
                      preload property
                    </Link>{' '}
                    of the <code>HTMLMediaElement</code> interface, with a few additional values:
                    <ul>
                      <li>
                        Specify <code>metadata</code> to load metadata immediately.
                      </li>
                      <li>
                        Specify <code>metadata_on_hover</code> to load metadata for the video only
                        when the user hovers over the player.
                      </li>
                      <li>
                        Specify <code>auto</code> to load metadata and the first few seconds of
                        video immediately.
                      </li>
                      <li>
                        Specify <code>auto_on_hover</code> to load metadata and the first few
                        seconds of video only when the user hovers over the player.
                      </li>
                      <li>
                        Specify <code>none</code> if you're not loading any data until playback
                        begins.
                      </li>
                    </ul>
                    Preloading more data makes playback start more quickly, so using the{' '}
                    <code>auto</code> and <code>auto_on_hover</code> values results in quicker
                    startup than the other options, but may use more bandwidth since video segments
                    are loaded regardless of a user playing the video. To learn more about Vimeo’s
                    bandwidth limits, visit our{' '}
                    <Link
                      variant="minimal"
                      href="https://help.vimeo.com/hc/en-us/articles/12426275404305-Bandwidth-on-Vimeo"
                    >
                      Help Center
                    </Link>
                    .
                  </span>,
                ],
                [
                  <code>
                    <b>progress_bar</b>
                  </code>,
                  <code>true</code>,
                  'Whether to show the progress bar in the embeddable player.',
                ],
                [
                  <code>
                    <b>quality</b>
                  </code>,
                  <code>auto</code>,
                  <span>
                    The playback quality of the video. Use <code>auto</code> for the best possible
                    quality given available bandwidth and other factors. Otherwise this field can
                    take any value from the quality menu in the player for the video. Common values
                    are <code>360p</code>, <code>720p</code>, <code>1080p</code>, <code>2k</code>,
                    and <code>4k</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>quality_selector</b>
                  </code>,
                  <code>true</code>,
                  'Whether to show the quality selector in the embeddable player.',
                ],
                [
                  <code>
                    <b>responsive</b>
                  </code>,
                  <code>false</code>,
                  <span>
                    Whether to return a <em>responsive embed code</em>, or one that provides
                    intelligent adjustments based on viewing conditions. We recommend this option
                    for mobile-optimized sites.
                  </span>,
                ],
                [
                  <code>
                    <b>skipping_forward</b>
                  </code>,
                  <code>true</code>,
                  'Whether users can skip forward in the embeddable player.',
                ],
                [
                  <code>
                    <b>speed</b>
                  </code>,
                  <code>true</code>,
                  'Whether to include playback speed among the player preferences.',
                ],
                [
                  <code>
                    <b>start_time</b>
                  </code>,
                  <code>0</code>,
                  <span>
                    In Segmented Playback mode, the time in seconds where playback starts for the
                    video; see the{' '}
                    <code>
                      <b>end_time</b>
                    </code>{' '}
                    field for more information.
                  </span>,
                ],
                [
                  <code>
                    <b>texttrack</b>
                  </code>,
                  'None',
                  <span>
                    The text track to display with the video. Specify the text track by its language
                    code (<code>en</code>), the language code and locale (<code>en-US</code>), or
                    the language code and kind (<code>en.captions</code>). For this argument to
                    work, the video must already have a text track of the given type; see our{' '}
                    <Link
                      variant="minimal"
                      href="https://vimeo.com/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426156795409-Captions-and-subtitles"
                    >
                      Help Center
                    </Link>{' '}
                    or{' '}
                    <Link variant="minimal" href="/api/upload/texttracks">
                      Working with Text Track Uploads
                    </Link>{' '}
                    for more information.
                    <div>
                      <br />
                      To enable automatically generated closed captions instead, provide the value{' '}
                      <code>en-x-autogen</code>. Please note that, at the present time, automatic
                      captions are always in English.
                    </div>
                  </span>,
                ],
                [
                  <code>
                    <b>thumbnail_id</b>
                  </code>,
                  'None',
                  "The ID of the thumbnail to load from the video's available thumbnail set.",
                ],
                [
                  <code>
                    <b>title</b>
                  </code>,
                  <code>true</code>,
                  "Whether to display the video's title.",
                ],
                [
                  <code>
                    <b>transcript</b>
                  </code>,
                  <code>true</code>,
                  'Whether transcript controls can appear in the embeddable player.',
                ],
                [
                  <code>
                    <b>transparent</b>
                  </code>,
                  <code>true</code>,
                  'Whether the responsive player and transparent background are enabled.',
                ],
                [
                  <code>
                    <b>unmute_button</b>
                  </code>,
                  <code>true</code>,
                  'Whether to display the unmute button.',
                ],
                [
                  <code>
                    <b>vimeo_logo</b>
                  </code>,
                  <code>true</code>,
                  'Whether to show the Vimeo logo in the embeddable player.',
                ],
                [
                  <code>
                    <b>volume</b>
                  </code>,
                  <code>true</code>,
                  'Whether to show the volume selector in the embeddable player.',
                ],
                [
                  <code>
                    <b>watch_full_video</b>
                  </code>,
                  <code>true</code>,
                  <span>
                    Whether to show the Watch Full Video button when Segmented Playback mode is
                    enabled; see{' '}
                    <code>
                      <b>start_time</b>
                    </code>{' '}
                    and{' '}
                    <code>
                      <b>end_time</b>
                    </code>{' '}
                    above for more information.
                  </span>,
                ],
                [
                  <code>
                    <b>width</b>
                  </code>,
                  'None',
                  'The width of the video in pixels.',
                ],
                [
                  <code>
                    <b>xhtml</b>
                  </code>,
                  <code>false</code>,
                  'Whether to make the embed code XHTML-compliant.',
                ],
              ]}
            />

            <Paragraph size="2">
              Under normal circumstances, the response contains various pieces of metadata in JSON
              format, including the video's embeddable <code>{`<iframe>`}</code> element in the{' '}
              <code>
                <b>html</b>
              </code>{' '}
              field.
            </Paragraph>

            <CodeBlock language="json">
              {{
                type: 'video',
                version: '1.0',
                provider_name: 'Vimeo',
                provider_url: 'https://vimeo.com/',
                title: 'My video',
                author_name: 'Sara Author',
                author_url: 'https://vimeo.com/techuser',
                is_plus: '0',
                account_type: 'live_business',
                html: '<iframe src="https://player.vimeo.com/video/286898202?h=fd61acd044" width="480" height="360" frameborder="0" title="My video" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>',
                width: 480,
                height: 360,
                duration: 23,
                description: 'This is my video.',
                thumbnail_url: 'https://i.vimeocdn.com/video/721904228_295x166',
                thumbnail_width: 295,
                thumbnail_height: 221,
                thumbnail_url_with_play_button:
                  'https://i.vimeocdn.com/filter/overlay?src0=https%3A%2F%2Fi.vimeocdn.com%2Fvideo%2F721904228_295x166&src1=http%3A%2F%2Ff.vimeocdn.com%2Fp%2Fimages%2Fcrawler_play.png',
                upload_date: '2020-10-20 10:00:00',
                video_id: 286898202,
                uri: '/videos/286898202',
              }}
            </CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> If you want a response in XML instead of in JSON, substitute{' '}
              <code>oembed.xml</code> for <code>oembed.json</code> in the URL.
            </StyledNotice>

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              for descriptions of these fields.
            </Paragraph>

            <GuideTable
              tableNumber={3}
              description="oEmbed response fields"
              headers={['Field', 'Description']}
              rows={[
                [
                  <code>
                    <b>type</b>
                  </code>,
                  <span>
                    The type of the oEmbed, which in this case is <code>video</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>version</b>
                  </code>,
                  'The version of the oEmbed spec.',
                ],
                [
                  <code>
                    <b>provider_name</b>
                  </code>,
                  <span>
                    The name of the oEmbed provider, which in this case is <code>Vimeo</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>provider_url</b>
                  </code>,
                  <span>
                    The URL of the oEmbed provider, which in this case is{' '}
                    <code>https://vimeo.com/</code>.
                  </span>,
                ],
                [
                  <code>
                    <b>title</b>
                  </code>,
                  'The title of the video.',
                ],
                [
                  <code>
                    <b>author_name</b>
                  </code>,
                  'The owner of the video.',
                ],
                [
                  <code>
                    <b>author_url</b>
                  </code>,
                  "The URL of the video's owner.",
                ],
                [
                  <code>
                    <b>is_plus</b>
                  </code>,
                  'This field is deprecated.',
                ],
                [
                  <code>
                    <b>account_type</b>
                  </code>,
                  "The video owner's membership type on Vimeo.",
                ],
                [
                  <code>
                    <b>html</b>
                  </code>,
                  <span>
                    The <code>{`<iframe>`}</code> element for embedding the video.
                  </span>,
                ],
                [
                  <code>
                    <b>width</b>
                  </code>,
                  'The width of the video in pixels.',
                ],
                [
                  <code>
                    <b>height</b>
                  </code>,
                  'The height of the video in pixels.',
                ],
                [
                  <code>
                    <b>duration</b>
                  </code>,
                  'The duration of the video in seconds.',
                ],
                [
                  <code>
                    <b>description</b>
                  </code>,
                  'The description of the video.',
                ],
                [
                  <code>
                    <b>thumbnail_url</b>
                  </code>,
                  <span>
                    The URL of the video's thumbnail image. For information about the image format
                    of the thumbnail, see our{' '}
                    <Link
                      variant="minimal"
                      href="/api/upload/thumbnails#returning-the-link-to-a-thumbnail"
                    >
                      Working with Thumbnail Uploads
                    </Link>{' '}
                    guide.
                  </span>,
                ],
                [
                  <code>
                    <b>thumbnail_width</b>
                  </code>,
                  <span>
                    The width of the video's thumbnail image in pixels, settable to the following
                    values: <code>100</code>, <code>200</code>, <code>295</code>, <code>640</code>,{' '}
                    <code>960</code>, and <code>1280</code>. For any other value, we return a
                    thumbnail at the next smallest width.
                  </span>,
                ],
                [
                  <code>
                    <b>thumbnail_height</b>
                  </code>,
                  <span>
                    The height of the video's thumbnail image in pixels. The returned height
                    automatically scales to the corresponding <code>thumbnail_width</code> value.
                  </span>,
                ],
                [
                  <code>
                    <b>thumbnail_url_with_play_button</b>
                  </code>,
                  <span>
                    The URL of the video's thumbnail image with a superimposed play button. For
                    information about the image format of the thumbnail, see our{' '}
                    <Link
                      variant="minimal"
                      href="/api/upload/thumbnails#returning-the-link-to-a-thumbnail"
                    >
                      Working with Thumbnail Uploads
                    </Link>{' '}
                    guide.
                  </span>,
                ],
                [
                  <code>
                    <b>upload_date</b>
                  </code>,
                  'The date and time when the video was uploaded.',
                ],
                [
                  <code>
                    <b>video_id</b>
                  </code>,
                  'The ID of the video.',
                ],
                [
                  <code>
                    <b>uri</b>
                  </code>,
                  'The URI of the video.',
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> If your video has{' '}
              <Link variant="minimal" href="/api/guides/videos/interact#set-off-site-privacy">
                domain-level privacy
              </Link>
              , you get back a slightly different response. See{' '}
              <Link variant="minimal" href="#embedding-videos-with-domain-privacy">
                Embedding videos with domain-level privacy
              </Link>{' '}
              below for more information.
            </StyledNotice>

            <Paragraph size="2">
              One last thing. If you've (very) recently updated the video that you're embedding, it
              might take a couple minutes before the oEmbed response reflects those changes.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Embed the video</Header>
            <Paragraph size="2">
              To embed the video, take the unescaped value of the{' '}
              <code>
                <b>html</b>
              </code>{' '}
              field, and include it in your HTML page.
            </Paragraph>

            <CodeBlock language="html">{`<html>
<head>
  <title>{page_title}</title>
</head>
<body>
  <iframe src="https://player.vimeo.com/video/{video_id}?h={hash_parameter}" width="{video_width}" height="{video_height}" frameborder="0" title="{video_title}" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>
</body>
</html>
          `}</CodeBlock>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="embedding-videos-with-domain-privacy">
          Embedding videos with domain-level privacy
        </Header>
        <Paragraph size="2">
          <Link variant="minimal" href="#embedding-a-video-with-oembed-step-1">
            Step 1
          </Link>{' '}
          in the previous section assumes that you're embedding a video with the <code>public</code>{' '}
          privacy setting. But for videos with the <code>whitelist</code> privacy setting — that is,
          videos with <em>domain-level privacy</em> — the results are a little different. The oEmbed
          request returns a simplified response containing no private metadata, like this:
        </Paragraph>

        <CodeBlock language="json">
          {{
            type: 'video',
            version: '1.0',
            provider_name: 'Vimeo',
            provider_url: 'https://vimeo.com/',
            html: '<iframe src="https://player.vimeo.com/video/286898202?h=fd61acd044" width="480" height="360" frameborder="0" title="My video" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>',
            width: 480,
            height: 360,
            domain_status_code: 403,
            video_id: 286898202,
            uri: '/videos/286898202',
          }}
        </CodeBlock>

        <Paragraph size="2">
          The embeddable <code>{`<iframe>`}</code> element appears in the{' '}
          <code>
            <b>html</b>
          </code>{' '}
          field.
        </Paragraph>

        <Paragraph size="2">
          To get the complete response, including the private metadata, send the{' '}
          <code>
            <b>Referer</b>
          </code>{' '}
          header with the request, and set its value to the video's permitted domain. Here's what
          the request looks like in cURL (with the{' '}
          <code>
            <b>-e</b>
          </code>{' '}
          option, which gives you the{' '}
          <code>
            <b>Referer</b>
          </code>{' '}
          header):
        </Paragraph>

        <CodeBlock>
          curl -e http://example.com
          https://vimeo.com/api/oembed.json?url=https:%2F%2Fvimeo.com%2F286898202
        </CodeBlock>

        <Paragraph size="2">
          For more information on privacy settings for embedded videos, see our{' '}
          <Link variant="minimal" href="/api/guides/videos/interact#set-off-site-privacy">
            Getting Started: Interacting with Videos
          </Link>{' '}
          guide.
        </Paragraph>

        <Header size="2" id="discovering-the-oembed-url">
          Discovering the oEmbed URL
        </Header>
        <Paragraph size="2">
          Under normal circumstances, the <code>{`<head>`}</code> section of a video's page on Vimeo
          includes two <code>{`<link>`}</code> elements: one with type{' '}
          <code>application/json+oembed</code>, and the other with type <code>text/xml+oembed</code>
          .
        </Paragraph>

        <CodeBlock language="html">{`<link rel="alternate" href="https://vimeo.com/api/oembed.json?url=https%3A%2F%2Fvimeo.com%2F{video_id}" type="application/json+oembed" title="{video_title}">

<link rel="alternate" href="https://vimeo.com/api/oembed.xml?url=https%3A%2F%2Fvimeo.com%2F{video_id}" type="text/xml+oembed" title="{video_title}">`}</CodeBlock>

        <Paragraph size="2">
          In these two links, the <code>href</code> attribute contains the URL for obtaining the
          oEmbed response in either JSON or XML format, depending on which one you use. These
          elements enable you to copy your video's URL and use it anywhere that oEmbed is supported.
        </Paragraph>

        <Header size="2" id="handling-errors">
          Handling errors
        </Header>
        <Paragraph size="2">
          Whenever oEmbed isn't working properly and you're sure that the video URL is correct,
          check the HTTP status code that you get back, and refer to{' '}
          <Link variant="minimal" href="#table-4">
            Table 4
          </Link>
          .
        </Paragraph>

        <GuideTable
          tableNumber={4}
          description="Probable causes for HTTP status codes"
          headers={['HTTP status code', 'Probable cause']}
          rows={[
            [
              304,
              <span>
                The video hasn't changed since the date given in the{' '}
                <code>
                  <b>If-Modified-Since</b>
                </code>{' '}
                HTTP header.
              </span>,
            ],
            [403, "Embed permissions are disabled for this video, so you can't embed it."],
            [
              404,
              "You aren't able to access the video because of privacy or permissions issues, or because the video is still transcoding.",
            ],
          ]}
        />

        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          Embedding standalone videos is one thing. But did you know that you can also use oEmbed
          with entire showcases of Vimeo videos to create interactive embeddable playlists? Learn
          all about it in our{' '}
          <Link variant="minimal" href="/api/oembed/showcases">
            Embedding Showcases
          </Link>{' '}
          guide.
        </Paragraph>

        <Paragraph size="2">
          If something isn't making sense, our first responders are ready to assist you.{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            Contact us
          </Link>{' '}
          to connect with a human.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
